import React, { useEffect } from 'react';
import { FormInput } from '../form-input';
import { formatCreditNumber, formatExpiry, translate } from '../../helper';
import { CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from "@stripe/react-stripe-js";
import StripeCardPaymentElement from '../stripe-card-payment-element';
import ButtonGradient from '../../common/button-gradient';
import TextBase from '../../common/text';
import {
  MESSAGE_TYPE,
  messageManager,
} from '../../helper';
import { Colors } from '../../themes/Colors';
import { useAttachedPaymentMethod } from '../../hooks/usePayment';
import { resolveCssVarValue } from '../../utils/CommonFunction';

export const CardForm = ({
  setValues,
  values,
  validation = {},
  setValidation = () => { },
  brand = '',
  disabled = false,
  goBack = () => { },
  useStripeCardPaymentElement = false,
}) => {
  const { attachedPaymentMethod, isAddingPaymentMethod, isError, error } =
    useAttachedPaymentMethod(onSuccess);

  const stripe = useStripe();
  const elements = useElements();
  const [stripeFontSize, setStripeFontSize] = React.useState(
    resolveCssVarValue('--fs-md2', { unit: 'px', fallback: '18px' })
  );
  useEffect(() => {
    const handleResize = () => {
      setStripeFontSize(
        resolveCssVarValue('--fs-md2', { unit: 'px', fallback: '18px' })
      );
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const onChange = (key, value) => {

    if (key === 'number') {
      if (formatCreditNumber(value).length <= 19) {
        setValues({
          ...values,
          number: formatCreditNumber(value)
        });
      }
    } else if (key === 'expiration_date') {
      setValues({
        ...values,
        expiration_date: formatExpiry(value)
      });
    } else if (key === 'cvc') {
      if (formatCreditNumber(value).length <= 4) {
        setValues({
          ...values,
          cvc: value
        });
      }
    } else {
      setValues({
        ...values,
        [key]: value
      });
    }
  };
  const cardStyle = {
    style: {
      base: {
        color: "#32325d",
        fontFamily: 'Arial, sans-serif',
        fontSmoothing: "antialiased",
        fontSize: stripeFontSize,
        background: '#ffffff',
        border: '1px solid #e5e5e5',
        "::placeholder": {
          color: "#cacaca",
          fontSize: stripeFontSize,
        }
      },
      invalid: {
        color: "#fa755a",
        iconColor: "#fa755a"
      }
    },
    hidePostalCode: true,
  };
  useEffect(() => {
    if (values?.card_holder?.length > 15 && !disabled) {
      setValidation({
        ...validation,
        card_holder: translate('cardHolderNameValidation')
      });
    }
  }, [values?.card_holder]);
  const handleChange = async (event) => {

  }
  const handleSubmit = async () => {
    const cardElement = elements.getElement(CardNumberElement);
    const { paymentMethod, error } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
      billing_details: {
        name: values?.card_holder
      }
    });
    if (error) {
      messageManager({
        messageShow: 'Error while adding card',
        type: MESSAGE_TYPE.error
      });
    } else {
      attachedPaymentMethod({ payment_id: paymentMethod.id });
    }
  }
  function onSuccess(data) {
    messageManager({
      messageShow: 'Card successfully added',
      type: MESSAGE_TYPE.success
    });
    goBack();
  }
  return (
    <div style={{ width: '100%' }}>
      <FormInput
        disabled={disabled}
        value={values?.card_holder}
        onChange={(e) => onChange('card_holder', e.target.value)}
        label={translate('cardHolderName')}
        placeholder={translate('enterCardHolderName')}
        error={validation?.card_holder}
      />
      {disabled && <>
        <FormInput
          disabled={disabled}
          value={values?.number}
          defaultValue={values?.number}
          onChange={(e) => onChange('number', e.target.value)}
          label={translate('cardNumber')}
          placeholder={translate('enterCardNumber')}
          error={validation?.number}
        />
        <FormInput
          disabled={disabled}
          value={values?.expiration_date}
          onChange={(e) => onChange('expiration_date', e.target.value)}
          label={translate('expirationDate')}
          placeholder={translate('enterExpirationDate')}
          error={validation?.expiration_date}
        />
        <FormInput
          disabled={disabled}
          value={values?.cvc}
          onChange={(e) => onChange('cvc', e.target.value)}
          label={translate('cvv')}
          placeholder={translate('enterCvv')}
          error={validation?.cvc}
        />
      </>}
      {!disabled && !useStripeCardPaymentElement && <>
        <div>
          <div
            className="no-select"
            style={{
              color: "rgb(0, 0, 0)",
              fontSize: 'var(--fs-md2)',
              fontWeight: 400,
              fontFamily: "Poppins, sans-serif",
              textAlign: "left",
            }}
          >
            {translate('cardNumber')}
          </div>
          <div style={{ height: 'calc(var(--fs-hero)*1.17924)', background: 'rgb(248, 248, 248)', marginTop: 'calc(var(--sp-xxs)*2)', border: 'solid 1px #d9d9d9', borderRadius: 'calc(var(--sp-xxs)*2)', padding: 'calc(var(--sp-md2)*1.41509) var(--sp-md2)' }}>
            <CardNumberElement
              id="card-element"
              class={'form-input-grey'}
              style={{ width: '70%', padding: 'calc(var(--sp-md2)*1.132075)', height: 'calc(var(--fs-xl)*1.886792)' }}
              options={cardStyle}
              onChange={handleChange}
            />
          </div>
        </div>
        <div>
          <div
            className="no-select"
            style={{
              color: "rgb(0, 0, 0)",
              fontSize: 'var(--fs-md2)',
              fontWeight: 400,
              fontFamily: "Poppins, sans-serif",
              textAlign: "left",
              marginTop: 'calc(var(--sp-md2)*1.886792)',
            }}
          >
            {translate('expiryDate')}
          </div>
          <div style={{ height: 'calc(var(--fs-hero)*1.17924)', background: 'rgb(248, 248, 248)', marginTop: 'calc(var(--sp-xxs)*2)', border: 'solid 1px #d9d9d9', borderRadius: 'calc(var(--sp-xxs)*2)', padding: 'calc(var(--sp-md2)*1.41509) var(--sp-md2)' }}>
            <CardExpiryElement
              id="card-element"
              class={'form-input-grey'}
              style={{ width: '70%', padding: 'calc(var(--sp-md2)*1.132075)', height: 'calc(var(--fs-xl)*1.886792)' }}
              options={cardStyle}
              onChange={handleChange}
            />
          </div>
        </div>
        <div style={{ marginBottom: 'calc(var(--sp-lg)*1.415094)' }}>
          <div
            className="no-select"
            style={{
              color: "rgb(0, 0, 0)",
              fontSize: 'var(--fs-md2)',
              fontWeight: 400,
              fontFamily: "Poppins, sans-serif",
              textAlign: "left",
              marginTop: 'calc(var(--sp-md2)*1.886792)',

            }}
          >
            {translate('cvv')}
          </div>
          <div style={{ height: 'calc(var(--fs-hero)*1.17924)', background: 'rgb(248, 248, 248)', marginTop: 'calc(var(--sp-xxs)*2)', border: 'solid 1px #d9d9d9', borderRadius: 'calc(var(--sp-xxs)*2)', padding: 'calc(var(--sp-md2)*1.41509) var(--sp-md2)' }}>
            <CardCvcElement
              id="card-element"
              class={'form-input-grey'}
              style={{ width: '70%', padding: 'calc(var(--sp-md2)*1.132075)', height: 'calc(var(--fs-xl)*1.886792)' }}
              options={cardStyle}
              placeholder={translate('enterCvv')}
              onChange={handleChange}
            />
          </div>
        </div>
      </>}
      {!disabled && useStripeCardPaymentElement && (
        <StripeCardPaymentElement onSuccess={onSuccess} goBack={goBack} />
      )}
      {isError && (
        <TextBase fontWeight={400} size={'calc(var(--fs-xs)*1.32075)'} color={Colors.red}>
          {error?.response?.data?.detail || translate('errorWhileAddingCard')}
        </TextBase>
      )}
      {!disabled && <ButtonGradient
        onClick={() => handleSubmit()}
        loading={isAddingPaymentMethod}
        textStyle={{ fontWeight: 700, fontSize: 'var(--fs-md2)' }}
        title={translate('proceed')}
        style={{ marginTop: 'calc(var(--sp-md2)*1.69811)', width: '100%' }}
      />}
    </div>
  );
};